import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Card, Popconfirm, Space, Tag, Typography } from 'antd';
import {
  queryTrainingAdvice,
  submitTrainingAdvice,
} from '@/services/trainingAdvice';
import { newRetryRequestId } from './FactSyncRetryButton';
import { processingTime } from './CommunicationProcessingPanel';
import {
  ADVICE_FAILURES,
  ADVICE_INTENTS,
  ADVICE_STATES,
  FOLLOW_UP_STATES,
  readTrainingAdvice,
} from './trainingAdvice';
import styles from './SimpleWorkspace.less';

const wrap = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };
const maySubmit = (data) =>
  data?.generationEnabled &&
  ['not_found', 'succeeded', 'failed'].includes(data.status) &&
  !(data.status === 'failed' && data.nextExecuteAt);

export default function TrainingAdvicePanel({ memberId, planId }) {
  const identity = `${memberId}:${planId}`;
  const active = useRef(identity);
  const command = useRef(null);
  const busy = useRef(false);
  const [state, setState] = useState({ identity });
  useEffect(() => {
    active.current = identity;
    command.current = null;
    busy.current = false;
    setState({ identity });
    return () => {
      active.current = null;
    };
  }, [identity]);
  const current = state.identity === identity ? state : {};
  const data = current.data;
  const load = async (exact = false) => {
    if (busy.current || active.current !== identity) return;
    busy.current = true;
    setState((old) => ({ ...old, identity, loading: true, error: '' }));
    const query = {
      memberId: Number(memberId),
      planId: Number(planId),
      ...(exact && data?.taskId ? { taskId: data.taskId } : {}),
    };
    try {
      const result = readTrainingAdvice(
        await queryTrainingAdvice({ requestData: query }),
        query,
      );
      if (active.current === identity)
        setState({ identity, data: result, loading: false });
    } catch (error) {
      if (active.current === identity)
        setState((old) => ({
          ...old,
          identity,
          loading: false,
          error:
            {
              401: '请先登录后台',
              403: '训练建议请求被服务拒绝，请检查登录状态和服务入口配置',
              503: '训练建议暂不可用或尚未启用',
            }[error?.response?.status] ||
            '暂时无法读取训练建议，请检查服务状态或稍后刷新。',
        }));
    } finally {
      busy.current = false;
    }
  };
  const submit = async () => {
    if (
      busy.current ||
      active.current !== identity ||
      (!current.uncertain && !maySubmit(data))
    )
      return;
    busy.current = true;
    if (!command.current)
      command.current = {
        memberId: Number(memberId),
        planId: Number(planId),
        requestId: newRetryRequestId(),
      };
    const original = { ...command.current };
    setState((old) => ({ ...old, identity, loading: true, error: '' }));
    try {
      const result = readTrainingAdvice(
        await submitTrainingAdvice({ requestData: original }),
        original,
        true,
      );
      if (active.current === identity) {
        const uncertain = current.uncertain && result.status !== 'accepted';
        if (!uncertain) command.current = null;
        setState({ identity, data: result, loading: false, uncertain });
      }
    } catch (error) {
      if (active.current === identity)
        setState((old) => ({
          ...old,
          identity,
          loading: false,
          uncertain: true,
          error: '提交结果未确认，请沿用原请求确认，不要反复新建请求。',
        }));
    } finally {
      busy.current = false;
    }
  };
  const result = data?.result;
  const sourceByRef = new Map(
    (result?.evidenceSources || []).map((source) => [source.ref, source]),
  );
  return (
    <Card
      size="small"
      title="训练建议与后续复评"
      className={styles.planDecisionCard}
    >
      <div className={styles.planDecisionHeader}>
        <div>
          <span className={styles.planDecisionEyebrow}>训练调整参考</span>
          <Typography.Paragraph
            type="secondary"
            className={styles.planDecisionIntro}
          >
            综合当前计划、实际训练、动作与课程评估及会员沟通，整理下一步可考虑的调整方向和复评重点。
          </Typography.Paragraph>
          <Typography.Paragraph
            type="secondary"
            className={styles.planSafetyHint}
          >
            建议不代表已经执行，也不会自动修改会员的在练计划。
          </Typography.Paragraph>
        </div>
        <Space wrap className={styles.planDecisionActions}>
          <Button disabled={current.loading} onClick={() => load(false)}>
            {current.loading
              ? '加载中…'
              : data
              ? '刷新当前建议'
              : '查看已有建议'}
          </Button>
          {data?.taskId && (
            <Button disabled={current.loading} onClick={() => load(true)}>
              查看本次结果
            </Button>
          )}
          {(current.uncertain || maySubmit(data)) && (
            <Popconfirm
              title={
                current.uncertain
                  ? '沿用原请求确认提交结果？'
                  : '确认根据当前冻结证据生成训练建议？相同证据不会重复调用模型。'
              }
              onConfirm={submit}
              okText="确认"
              cancelText="取消"
            >
              <Button type="primary" disabled={current.loading}>
                {current.uncertain ? '沿用原请求确认' : '生成/复评建议'}
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>
      {current.error && (
        <Alert
          className={styles.planDecisionAlert}
          type="warning"
          message={current.error}
        />
      )}
      {data && (
        <div
          className={`${styles.planTaskState} ${
            data.status === 'succeeded'
              ? styles.planTaskSucceeded
              : data.status === 'failed'
              ? styles.planTaskFailed
              : styles.planTaskPending
          }`}
        >
          <strong>{ADVICE_STATES[data.status]}</strong>
          {!data.generationEnabled && <span>当前配置不允许新生成</span>}
        </div>
      )}
      {data?.taskId && (
        <details className={styles.planTaskDetails}>
          <summary>处理信息</summary>
          <Typography.Paragraph type="secondary" style={wrap}>
            任务编号：{data.taskId}
            {data.attemptCount != null
              ? ` · 已执行 ${data.attemptCount} 次`
              : ''}
            {data.capturedAt
              ? ` · 证据截止：${processingTime(data.capturedAt)}`
              : ''}
            {data.nextExecuteAt
              ? ` · 下次执行：${processingTime(data.nextExecuteAt)}`
              : ''}
          </Typography.Paragraph>
        </details>
      )}
      {data?.failureCode && (
        <Alert
          className={styles.planDecisionAlert}
          type="warning"
          message={ADVICE_FAILURES[data.failureCode]}
          description={
            data.nextExecuteAt
              ? '后台已安排重试，请勿重复提交。'
              : '当前任务没有生成有效建议。'
          }
        />
      )}
      {result && (
        <>
          <div className={styles.adviceResultHeader}>
            <div>
              <strong>当前建议</strong>
              <span>共 {result.suggestions.length} 条</span>
            </div>
            <span>模型：{data.model}</span>
          </div>
          {!result.suggestions.length && (
            <div className={styles.adviceEmpty}>
              当前证据不足，未生成训练建议
            </div>
          )}
          <div className={styles.adviceList}>
            {result.suggestions.map((item, index) => (
              <article key={item.adviceId} className={styles.adviceCard}>
                <div className={styles.adviceHeading}>
                  <span className={styles.adviceSequence}>{index + 1}</span>
                  <div>
                    <Tag
                      color={
                        item.intent === 'ADJUSTMENT_DIRECTION' ? 'blue' : 'gold'
                      }
                    >
                      {ADVICE_INTENTS[item.intent]}
                    </Tag>
                    <strong>{item.direction}</strong>
                  </div>
                </div>
                <div className={styles.adviceBody}>
                  <div>
                    <span>为什么这样建议</span>
                    <p>{item.reason}</p>
                  </div>
                  <div>
                    <span>后续重点观察</span>
                    <p>{item.expectedObservation}</p>
                  </div>
                </div>
                <Typography.Paragraph className={styles.adviceLimitations}>
                  适用限制：{item.limitations.join('；')}
                </Typography.Paragraph>
                <details className={styles.adviceEvidence}>
                  <summary>查看依据</summary>
                  <ul>
                    {item.evidenceRefs.map((ref) => (
                      <li key={ref}>{sourceByRef.get(ref)?.text || ref}</li>
                    ))}
                  </ul>
                </details>
              </article>
            ))}
          </div>
          {!!result.followUps.length && (
            <section className={styles.adviceFollowUps}>
              <strong>既往建议的后续观察</strong>
              {result.followUps.map((item) => (
                <div key={item.adviceId}>
                  <Tag>{FOLLOW_UP_STATES[item.status]}</Tag>
                  <span>{item.summary}</span>
                </div>
              ))}
            </section>
          )}
          {!!result.missingEvidence.length && (
            <Alert
              className={styles.planDecisionAlert}
              type="info"
              message="当前还缺少部分判断依据"
              description={result.missingEvidence.join('；')}
            />
          )}
          <Typography.Paragraph className={styles.planAssessmentBoundary}>
            建议的实施状态尚未验证，因果关系也未建立。后续出现新记录，只表示获得了新证据，不代表建议已经执行或产生效果。
          </Typography.Paragraph>
        </>
      )}
    </Card>
  );
}

TrainingAdvicePanel.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  planId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
TrainingAdvicePanel.defaultProps = { memberId: null, planId: null };
